/**
 * Owner login redirect to incomplete-location onboarding
 * (DESIGN_DELTA_2026-07-23 §4, PR #356).
 *
 * Pure helpers + session guard behind the LandingRedirect auto-redirect: when
 * the multi-location flag is ON and the signed-in user OWNS a location whose
 * initial onboarding is incomplete, the app switches them into that location's
 * onboarding after login (the existing sub-flow B then resumes the wizard at
 * the saved stage, e.g. `about_your_business`).
 *
 * DATA SOURCE (swap point): `/access` `available_locations` carries NO
 * per-location onboarding state — it only has identity/address/role. So
 * completeness comes from the onboarding microservice the app already calls:
 * `GET /onboardings?salonId=<salon_uuid>` via
 * `AppContext.getOnboardingProgress`. The probe lives in
 * `useOwnerOnboardingRedirect`; if `/access` ever grows per-location
 * onboarding fields, swap the probe there and keep these pure helpers.
 *
 * Identifiers stay `salon`-scoped verbatim (no rename); only UI copy says
 * "location".
 */
import type { AvailableLocation } from "../types/featureAccess";
import type { OnboardingProgressResponse } from "../components/onboarding/types/onboarding";

export interface LocationOnboardingProbe {
  location: AvailableLocation;
  /**
   * `null` = no onboarding row exists (backend returns `{}`) or the probe
   * failed. Deliberately treated as NOT incomplete — legacy salons without
   * onboarding rows must never yank their owner into the wizard. The real
   * new-location flow always has a row: support seeds `salon_onboarding`
   * (state `inprogress`, stage `about_your_business`) when creating the
   * location.
   */
  progress: OnboardingProgressResponse | null;
}

/**
 * Locations that can trigger the redirect: OWNED by this user and not the one
 * currently being viewed. The selected location's own onboarding state is
 * already handled by the pre-existing LandingRedirect gate (`/access`
 * `onboarding` is scoped to the selected salon), so it is excluded here.
 * `selectedSalonUuid` (= `businessInfo.id`, which GET /business-locations returns as the
 * salon UUID) is a belt-and-braces second exclusion alongside the numeric id.
 */
export function getOwnedCandidateLocations(
  availableLocations: AvailableLocation[],
  selectedSalonId: number | null,
  selectedSalonUuid?: string | null,
): AvailableLocation[] {
  return availableLocations.filter(
    (loc) =>
      loc.role === "Owner" &&
      loc.salon_id !== selectedSalonId &&
      (selectedSalonUuid == null || loc.salon_uuid !== selectedSalonUuid),
  );
}

/**
 * Incomplete = an EXPLICIT onboarding row whose initial onboarding is not
 * "complete" ("null" / "inprogress"). Progressive-only gaps do NOT trigger the
 * cross-location redirect — sub-flow B is about the initial wizard.
 */
export function isInitialOnboardingIncomplete(
  probe: LocationOnboardingProbe,
): boolean {
  if (!probe.progress) return false;
  return probe.progress.initialOnboarding !== "complete";
}

/**
 * When multiple owned locations are incomplete, redirect into the most
 * recently created one. Recency comes from the onboarding row's `createdDt`
 * (support creates the row together with the location); locations whose probe
 * lacks a timestamp sort last, tie-broken by higher `salon_id` (serial ids
 * grow with creation order).
 */
export function pickMostRecentlyCreated(
  probes: LocationOnboardingProbe[],
): LocationOnboardingProbe | null {
  if (probes.length === 0) return null;
  const sorted = [...probes].sort((a, b) => {
    const aTime = a.progress?.createdDt ? Date.parse(a.progress.createdDt) : 0;
    const bTime = b.progress?.createdDt ? Date.parse(b.progress.createdDt) : 0;
    if (aTime !== bTime) return bTime - aTime;
    return b.location.salon_id - a.location.salon_id;
  });
  return sorted[0];
}

// ---------------------------------------------------------------------------
// Session guard — this is what keeps the redirect from being a hard gate.
//
// Set right before the auto-redirect fires. It survives the full page reload
// the location switch performs (sessionStorage, same tab), so when the owner
// later uses the LocationPicker to escape to a completed location
// mid-onboarding, landing on "/" does NOT yank them back. Cleared on logout
// (AuthContext.resetAuth) so the next sign-in re-evaluates.
// ---------------------------------------------------------------------------

const GUARD_KEY = "pagoda.ownerOnboardingAutoRedirect.done";

export function hasOwnerOnboardingRedirectRun(): boolean {
  try {
    return window.sessionStorage.getItem(GUARD_KEY) === "1";
  } catch {
    // sessionStorage unavailable (private mode / SSR) → behave as already run
    // so we degrade to "no auto-redirect" rather than an un-escapable loop.
    return true;
  }
}

export function markOwnerOnboardingRedirectRun(): void {
  try {
    window.sessionStorage.setItem(GUARD_KEY, "1");
  } catch {
    // Best-effort; failure just means the guard read stays conservative.
  }
}

export function clearOwnerOnboardingRedirectGuard(): void {
  try {
    window.sessionStorage.removeItem(GUARD_KEY);
  } catch {
    // Best-effort.
  }
}
